/**
 * Custom RTK Query baseQuery backed by our Axios instance.
 *
 * The Coral backend uses awsJson-style dispatch: every operation is a POST with
 * the operation name in the `X-Amz-Target` header and a JSON body. Dispatch is by
 * that header — the backend ignores the URL path — so we POST to a per-operation
 * path (`/api/<Operation>`) purely so the browser DevTools Network panel shows a
 * readable name per call instead of a wall of identical `/` rows.
 *
 * Feature API files specify `operation` (the Smithy operation name) and `body`
 * (the input structure). This baseQuery translates that into the wire format.
 */
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import apiClient from './apiClient';
import type { ApiError } from './apiTypes';

export interface CoralQueryArgs {
  operation: string;
  body?: Record<string, unknown> | object;
  /** Opt out of the apiClient's blanket 401/403 → /auth-error redirect. */
  skipAuthRedirect?: boolean;
}

interface CoralQueryError {
  status: number | undefined;
  data: ApiError | unknown;
}

const SERVICE_NAME = 'PIChecklistPortalService';

const axiosBaseQuery: BaseQueryFn<
  CoralQueryArgs,
  unknown,
  CoralQueryError
> = async (args) => {
  const config: AxiosRequestConfig & { skipAuthRedirect?: boolean } = {
    url: `/api/${args.operation}`,
    method: 'POST',
    data: args.body ?? {},
    headers: {
      'Content-Type': 'application/x-amz-json-1.0',
      'X-Amz-Target': `${SERVICE_NAME}.${args.operation}`,
    },
    skipAuthRedirect: args.skipAuthRedirect,
  };

  try {
    const result = await apiClient(config);
    return { data: result.data };
  } catch (err) {
    const axiosError = err as AxiosError<ApiError>;
    return {
      error: {
        status: axiosError.response?.status,
        data: axiosError.response?.data ?? { message: axiosError.message },
      },
    };
  }
};

export default axiosBaseQuery;
